/**
 * Cell provenance for the cascading OVS split editors — pure, so the
 * inherited-vs-overridden rules are unit-testable without React.
 *
 * The server owns resolution (`packages/schema/src/managers/rate-cascade.ts`).
 * These helpers answer the two questions the EDITORS need, which resolution
 * alone does not:
 *
 *   1. Does THIS tier own this cell, or is the number inherited?
 *   2. If inherited, what value should render as a PLACEHOLDER?
 *
 * ⚠ The load-bearing invariant, restated because every caller depends on it:
 * **an inherited value that is merely displayed must never be persisted.**
 * `SplitCell.value` is `null` for an inherited cell precisely so a controlled
 * input cannot round-trip the placeholder into a stored row. Writing an
 * inherited value detaches this tier from the one above it permanently, and a
 * later district edit silently stops reaching it.
 */

import type { Mealtime } from '@/lib/mealtime'

/** Which cascade tier owns a value. */
export type SplitScope = 'ORGANIZATION' | 'LOCATION' | 'LOCATION_WEEKDAY' | 'LOCATION_DAY'

/** The mealtime/column scope key of one stored row. */
export interface SplitRowScope {
  groupDimension: string | null
  groupValue: string | null
  mealtime: Mealtime | null
}

/** A stored delivery-rate row, as the settings API returns it. */
export interface SplitRateRow extends SplitRowScope {
  componentKind: string
  deliveryRateBps: number
}

/** A stored milk-share row, as the settings API returns it. */
export interface SplitShareRow extends SplitRowScope {
  componentId: string
  shareBps: number
}

/**
 * One editable cell.
 *
 * `value` is the number THIS tier stores, or `null` when the cell is
 * inherited. `inheritedBps` is what the tier above resolves to, for the
 * placeholder. `owned` is `value !== null` — kept as its own field so call
 * sites read as intent rather than as a null check.
 */
export interface SplitCell {
  /** This tier's stored value; `null` = inherited (nothing stored here). */
  value: number | null
  /** The tier above's value, for the placeholder. `null` = nothing above either. */
  inheritedBps: number | null
  /** True when this tier stores a row for the cell — i.e. an OVERRIDE. */
  owned: boolean
  /** Where the EFFECTIVE value comes from. `null` = the platform 100%. */
  source: SplitScope | null
}

/** Map key for one scoped row: `<mealtime|legacy>::<columnKey|default>`. */
export function splitRowScopeKey(row: SplitRowScope, columnKeyOf: (row: SplitRowScope) => string | null): string {
  return `${row.mealtime ?? 'legacy'}::${columnKeyOf(row) ?? 'default'}`
}

/**
 * The four mealtime/column tiers for one cell, MOST → least specific. Callers
 * walk this in order and take the first hit; the index tells them which tier
 * matched, and index 0 is the only "this mealtime owns this exact cell" case.
 */
export function splitCellScopeKeys(mealtime: Mealtime, columnKey: string): string[] {
  return [...new Set([`${mealtime}::${columnKey}`, `${mealtime}::default`, `legacy::${columnKey}`, `legacy::default`])]
}

/**
 * Resolve one delivery-rate cell across the campus's own rows and the
 * district's, with provenance.
 *
 * `ownRows` are the rows THIS tier stores; `inheritedRows` are the tier
 * above's. Only `ownRows` can make a cell `owned`, and only `ownRows` values
 * are ever eligible to be written back.
 *
 * The terminal fallback is the platform 100% with `source: null`. That is
 * DISTINCT from a stored 10000 with `source: 'LOCATION'` — an explicitly
 * stored 100% is a real override of the district, and the two must never
 * render identically or an admin will "clean up" the override and change how
 * much food ships.
 */
export function resolveRateCell(args: {
  ownRows: readonly SplitRateRow[]
  inheritedRows: readonly SplitRateRow[]
  componentKind: string
  mealtime: Mealtime
  columnKey: string
  columnKeyOf: (row: SplitRowScope) => string | null
}): SplitCell {
  const { ownRows, inheritedRows, componentKind, mealtime, columnKey, columnKeyOf } = args
  const keys = splitCellScopeKeys(mealtime, columnKey)

  const pick = (rows: readonly SplitRateRow[]): { bps: number; tierIndex: number } | null => {
    for (const [tierIndex, key] of keys.entries()) {
      const row = rows.find(
        (candidate) => candidate.componentKind === componentKind && splitRowScopeKey(candidate, columnKeyOf) === key,
      )
      if (row) {
        return { bps: row.deliveryRateBps, tierIndex }
      }
    }
    return null
  }

  const inherited = pick(inheritedRows)
  const own = pick(ownRows)
  if (own) {
    return {
      // Only a tier-0 hit (this mealtime, this exact column) is an override the
      // user can edit in place; a broader own-row is displayed but inherited
      // FROM ITSELF at a less specific tier, so typing must create the
      // specific row rather than mutate the broader one.
      value: own.tierIndex === 0 ? own.bps : null,
      inheritedBps: own.tierIndex === 0 ? (inherited?.bps ?? null) : own.bps,
      owned: own.tierIndex === 0,
      source: 'LOCATION',
    }
  }
  return {
    value: null,
    inheritedBps: inherited?.bps ?? null,
    owned: false,
    source: inherited ? 'ORGANIZATION' : null,
  }
}

/**
 * Resolve the milk-share cells for one (mealtime × column) across both tiers.
 *
 * ⚠ Milk shares resolve as a WHOLE SET per tier: the campus owns the ENTIRE
 * split for a cell as soon as it stores ANY share row for it. So this returns
 * the whole set at once and `setOwnedHere` is a property of the CELL, not of
 * an individual SKU.
 *
 * The practical consequence for the editor: when the user first overrides one
 * SKU, it must write the COMPLETE resolved set (not just the edited SKU), or
 * the district's other SKUs resolve to an implicit 0 and that milk stops
 * shipping. `milkSetWriteForOverride` builds that payload.
 */
export function resolveShareCells(args: {
  ownRows: readonly SplitShareRow[]
  inheritedRows: readonly SplitShareRow[]
  skuIds: readonly string[]
  mealtime: Mealtime
  columnKey: string
  columnKeyOf: (row: SplitRowScope) => string | null
  /** Even-split bps per SKU, used when NO tier configured the cell. */
  evenBps: readonly number[]
}): { cells: Record<string, SplitCell>; setOwnedHere: boolean } {
  const { ownRows, inheritedRows, skuIds, mealtime, columnKey, columnKeyOf, evenBps } = args
  const keys = splitCellScopeKeys(mealtime, columnKey)

  /** The merged set for one tier across its four mealtime/column passes. */
  const setFor = (rows: readonly SplitShareRow[]): Record<string, number> | null => {
    const merged: Record<string, number> = {}
    let sawAny = false
    // Least → most specific so the more specific row wins per SKU, matching
    // `overlayScopedShares`'s within-scope per-SKU merge.
    for (const key of [...keys].reverse()) {
      for (const row of rows) {
        if (splitRowScopeKey(row, columnKeyOf) === key) {
          merged[row.componentId] = row.shareBps
          sawAny = true
        }
      }
    }
    return sawAny ? merged : null
  }

  const ownSet = setFor(ownRows)
  const inheritedSet = setFor(inheritedRows)
  const cells: Record<string, SplitCell> = {}
  skuIds.forEach((skuId, index) => {
    // A SKU missing from a configured set is 0, not the even split: the
    // configured shares are the whole pie (mirrors `MilkShareResolver`).
    const inheritedBps = inheritedSet ? (inheritedSet[skuId] ?? 0) : (evenBps[index] ?? null)
    if (ownSet) {
      cells[skuId] = {
        value: ownSet[skuId] ?? 0,
        inheritedBps,
        owned: true,
        source: 'LOCATION',
      }
      return
    }
    cells[skuId] = {
      value: null,
      inheritedBps,
      owned: false,
      source: inheritedSet ? 'ORGANIZATION' : null,
    }
  })
  return { cells, setOwnedHere: ownSet !== null }
}

/**
 * The COMPLETE milk-share set to write when the user overrides one SKU at a
 * cell this tier does not yet own.
 *
 * Writing only the edited SKU would make this tier own a one-row set, and
 * because shares resolve as a whole set per tier the other SKUs would resolve
 * to an implicit 0 — that milk silently stops shipping. So materialising the
 * inherited siblings alongside the edit is REQUIRED, and it is not a violation
 * of "never persist an inherited value": for milk the SET is the unit of
 * override, so the whole set is what the user is overriding.
 *
 * Returns `null` when the tier already owns the set (nothing to materialise —
 * write just the edited SKU).
 */
export function milkSetWriteForOverride(args: {
  cells: Record<string, SplitCell>
  setOwnedHere: boolean
  editedComponentId: string
  editedBps: number
}): Array<{ componentId: string; shareBps: number }> | null {
  const { cells, setOwnedHere, editedComponentId, editedBps } = args
  if (setOwnedHere) {
    return null
  }
  return Object.entries(cells).map(([componentId, cell]) => ({
    componentId,
    shareBps: componentId === editedComponentId ? editedBps : (cell.inheritedBps ?? 0),
  }))
}

/**
 * Human label for a cell's provenance chip.
 *
 * `weekday` is only needed for the recurring tier, so that the chip can say
 * "Fridays at this campus" rather than the useless "this weekday".
 */
export function splitSourceLabel(source: SplitScope | null, weekday?: string | null): string {
  if (source === 'ORGANIZATION') {
    return 'District'
  }
  if (source === 'LOCATION') {
    return 'This campus'
  }
  if (source === 'LOCATION_WEEKDAY') {
    return weekday ? `${WEEKDAY_PLURALS[weekday] ?? weekday} at this campus` : 'One weekday at this campus'
  }
  if (source === 'LOCATION_DAY') {
    return 'This date'
  }
  return 'Default (100%)'
}

/** "FRIDAY" → "Fridays" — a recurring rule reads as a plural. */
const WEEKDAY_PLURALS: Record<string, string> = {
  MONDAY: 'Mondays',
  TUESDAY: 'Tuesdays',
  WEDNESDAY: 'Wednesdays',
  THURSDAY: 'Thursdays',
  FRIDAY: 'Fridays',
  SATURDAY: 'Saturdays',
  SUNDAY: 'Sundays',
}

/**
 * The Straight Serve "Milk split" card's per-mealtime summary line:
 * "Whole Milk 95% • Lactose-Free Milk 5%" — one entry per lineup SKU,
 * showing each cell's DISPLAYED value (own value, else the inherited
 * placeholder, else 0) for the FIRST resolved column set. Column-scoped
 * values can't render in one line, so when any later column's displayed
 * split deviates from the first the line ends with a single
 * "varies by group" marker — mirroring `formatMealtimeSplitSummary`.
 *
 * Percentages here are bps/100 with trailing zeros trimmed (5000 → "50",
 * 3333 → "33.33") — the same rendering the grid's inputs use.
 */
export function formatMilkShareSummary(
  skus: ReadonlyArray<{ id: string; name: string }>,
  sets: ReadonlyArray<{ cells: Record<string, SplitCell> }>,
): string {
  const base = sets[0]
  if (!base || skus.length === 0) {
    return ''
  }
  const displayedBps = (set: { cells: Record<string, SplitCell> }, skuId: string): number => {
    const cell = set.cells[skuId]
    return cell?.value ?? cell?.inheritedBps ?? 0
  }
  const formatPct = (bps: number): string => {
    const pct = bps / 100
    return Number.isInteger(pct) ? String(pct) : pct.toFixed(2).replace(/\.?0+$/, '')
  }
  const parts = skus.map((sku) => `${sku.name} ${formatPct(displayedBps(base, sku.id))}%`)
  const variesByGroup = sets
    .slice(1)
    .some((set) => skus.some((sku) => displayedBps(set, sku.id) !== displayedBps(base, sku.id)))
  return variesByGroup ? `${parts.join(' • ')} • varies by group` : parts.join(' • ')
}

// ---------------------------------------------------------------------------
// Legacy split opt-outs (Sheila, 2026-08-26: OVS cannot disable splits)
// ---------------------------------------------------------------------------

/** The stored split modes of the two tiers, as `componentSplit` reports them. */
export interface SplitOptOutState {
  /** The campus's own stored mode. `null` = inheriting the district. */
  locationMode: 'ENABLED' | 'DISABLED' | null
  /** The district's stored mode. `null` = no district opt-out. */
  organizationMode: 'ENABLED' | 'DISABLED' | null
  /** The RESOLVED mode — campus, else district, else null. */
  mode: 'ENABLED' | 'DISABLED' | null
}

/**
 * Whether an OVS campus is still held off by a stored opt-out.
 *
 * `DISABLED` is no longer authorable for OVS, but rows written before
 * 2026-08-26 still resolve — and resolution is deliberately untouched, because
 * silently turning a campus on would ship food nobody ordered. So this state is
 * LEGACY-only: reachable in the database, never reachable by a new edit, and it
 * gets its own explanatory notice rather than an off switch.
 */
export function hasLegacySplitOptOut(split: SplitOptOutState): boolean {
  return split.mode === 'DISABLED'
}

/**
 * The campus-tier `componentSplitMode` write that clears a legacy opt-out —
 * the one-way exit that replaces the retired on/off switch.
 *
 * `null` (inherit) whenever the district itself carries no opt-out: stamping
 * `ENABLED` there would detach the campus from a future district-wide change,
 * which is the mistake the cascade exists to avoid.
 *
 * `'ENABLED'` when the district IS opted out, because clearing the campus row
 * would only make it inherit that `DISABLED` and the campus would stay off.
 */
export function clearSplitOptOutMode(split: SplitOptOutState): 'ENABLED' | null {
  return split.organizationMode === 'DISABLED' ? 'ENABLED' : null
}
